import { ButtonProps, extendTheme } from "@chakra-ui/react";
import colors from "app/colors";
import { DRAWER_WIDTH_PX } from "modules/utils/Constants";
import { checkboxTheme } from "./checkbox-theme";
import { inputTheme } from "./input-theme";

const base = {
  styles: {
    global: {
      body: {
        overflow: "hidden",
      },
    },
  },
  fonts: {
    heading: `var(--font-inter), sans-serif`,
    body: `var(--font-inter), sans-serif`,
    // token entries so the many `fontFamily="Inter"` / `fontFamily="Outfit"`
    // style props across the app resolve to the next/font families (the
    // literal family names no longer exist — next/font registers hashed
    // family names exposed through these CSS variables)
    Inter: `var(--font-inter), sans-serif`,
    Outfit: `var(--font-outfit), sans-serif`,
  },
  components: {
    Button: {
      defaultProps: {
        variant: "solid",
      },
      variants: {
        solid: (): ButtonProps => ({
          background: colors.orangePrimary,
          color: "white",
          fontWeight: "normal",
          fontSize: "sm",
          rounded: "md",
          _disabled: {
            // !important prevents _hover or _active from firing
            background: `${colors.orangePrimary} !important`,
            opacity: "0.6 !important",
          },
          _hover: {
            bg: colors.orangePrimary,
            opacity: 0.8,
          },
          _active: {
            bg: colors.orangePrimary,
            opacity: 0.5,
          },
        }),
        outline: (): ButtonProps => ({
          borderColor: colors.gray0,
          fontWeight: "normal",
          fontSize: "sm",
          rounded: "md",
          _disabled: {
            // !important prevents _hover or _active from firing
            background: `${colors.darkBlue}0F !important`, // 15%
          },
          _hover: {
            bg: `${colors.darkBlue}0A`, // 10%
          },
          _active: {
            bg: `${colors.darkBlue}19`, // 25%
          },
        }),
        ghost: (): ButtonProps => ({
          color: colors.darkBlue,
          fontWeight: "semibold",
          fontSize: "sm",
          rounded: "md",
          _disabled: {
            // !important prevents _hover or _active from firing
            background: `${colors.darkBlue}0F !important`, // 15%
          },
          _hover: {
            bg: `${colors.darkBlue}0A`, // 10%
          },
          _active: {
            bg: `${colors.darkBlue}19`, // 25%
          },
        }),
        link: ({ color }: ButtonProps): ButtonProps => ({
          color: color ?? "unset",
          fontWeight: 500,
          _disabled: {
            opacity: 0.5,
          },
          _hover: {
            opacity: 0.6, // value matches link.tsx
            textDecoration: "none",
          },
          _active: {
            opacity: 0.4, // value matches link.tsx
            color: color ?? "unset",
          },
        }),
        cancel: (): ButtonProps => ({
          background: colors.gray40,
          color: colors.white,
          fontWeight: "normal",
          fontSize: "sm",
          rounded: "md",
          _disabled: {
            // !important prevents _hover or _active from firing
            background: `${colors.gray40} !important`,
            opacity: "0.6 !important",
          },
          _hover: {
            bg: colors.gray40,
            opacity: 0.8,
          },
          _active: {
            bg: colors.gray40,
            opacity: 0.5,
          },
        }),
        hover: (): ButtonProps => ({
          rounded: "md",
          _hover: {
            opacity: 0.6,
            textDecoration: "none",
          },
          _disabled: {
            background: `${colors.gray40} !important`,
            opacity: "0.6 !important",
          },
        }),
        custom: (): ButtonProps => ({}),
      },
    },
    Drawer: {
      parts: ["dialog", "header", "body"],
      variants: {
        synco: {
          dialog: {
            minW: DRAWER_WIDTH_PX,
          },
        },
      },
    },
    Textarea: {
      baseStyle: {
        px: 2,
      },
    },
    Input: inputTheme,
    Checkbox: checkboxTheme,
  },
};

export const chakraTheme = extendTheme(base);
